import Link from "next/link"
import { ArrowUpRight } from "lucide-react"
import { SectionRule } from "./section-rule"

export interface ToolProduct {
  brand: string
  model: string
  price: string
  /** Annual energy / kWh / other one-line spec — free-form string */
  specs?: string
  /** Optional savings line (e.g. "$34/yr less than avg") */
  badge?: string
  /** Product detail page URL */
  href: string
  /** Whether to show the ENERGY STAR pill */
  energyStar?: boolean
}

interface ToolProductRailProps {
  products: ToolProduct[]
  eyebrow?: string
  /** Section title. Omit (or pass empty string) to render without a header — useful when the parent already provides a SectionRule. */
  title?: string
  /** Optional right-aligned meta (e.g. "Updated daily") */
  meta?: string
  /** Narrow (~1120) or wide (~1320) container */
  width?: "narrow" | "wide"
  className?: string
}

/**
 * ToolProductRail — 3-up product card rail for tool-page variants.
 * Keeps the editorial language of the index page (black rule section
 * header, №01-style index, brand label above model, orange "Compare
 * prices" CTA).
 *
 * Not a Supabase-live carousel — accepts a prebuilt products array so
 * each variant can pass whatever matches its theme (ENERGY STAR fridges
 * on the calculator, French door picks on the comparison page, etc.).
 * Swap to a data-driven version later by replacing the `products` array
 * with a `getTrendingInCollection(slug)` call at the page-client level.
 */
export function ToolProductRail({
  products,
  eyebrow,
  title,
  meta,
  width = "narrow",
  className = "",
}: ToolProductRailProps) {
  const maxW = width === "wide" ? "max-w-[1320px]" : "max-w-[1120px]"
  const showHeader = !!title
  return (
    <section className={`bg-white py-8 md:py-12 ${className}`}>
      <div className={`${maxW} mx-auto px-6 md:px-8`}>
        {showHeader && <SectionRule eyebrow={eyebrow} title={title!} right={meta} />}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {products.map((p, i) => (
            <Link
              key={p.model}
              href={p.href}
              className="group block rounded-xl p-5 bg-white border border-gray-200 transition-all hover:-translate-y-1 hover:border-[#ff9951] hover:shadow-[0_20px_25px_-5px_rgba(255,153,81,0.15),0_8px_10px_-6px_rgba(255,153,81,0.08)]"
            >
              {/* Image placeholder */}
              <div
                className="aspect-[4/3] rounded-lg mb-4 flex items-center justify-center"
                style={{ background: "rgba(4,105,240,0.04)" }}
              >
                <div
                  className="w-16 h-24 rounded-sm"
                  style={{
                    background:
                      "linear-gradient(180deg, #d1d5db 0%, #9ca3af 60%, #d1d5db 60%, #9ca3af 100%)",
                  }}
                />
              </div>

              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#0469f0]">
                  {p.brand}
                </span>
                <span className="font-mono text-[11px] text-gray-500">
                  № {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="text-[16px] font-extrabold tracking-tight mb-2 font-mono text-gray-900">
                {p.model}
              </div>
              {p.specs && (
                <div className="text-[12px] text-gray-500 mb-3">{p.specs}</div>
              )}
              {p.badge && (
                <div className="inline-block text-[11px] font-bold px-2 py-1 rounded-full bg-[#ff9951]/10 text-[#ff9951] mb-3">
                  {p.badge}
                </div>
              )}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className="text-[18px] font-black tracking-tighter text-gray-900">
                  {p.price}
                </span>
                <div className="flex items-center gap-1.5">
                  {p.energyStar && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white bg-[#0469f0]">
                      ★
                    </span>
                  )}
                  <span className="text-[12px] font-bold flex items-center gap-1 text-[#ff9951]">
                    Compare prices <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
